import * as React from "react";
import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import type { SlideRef, TabsState } from "../types";
import {
  loadTabsState,
  loadTabsStateFromBackend,
  saveTabsStateForDocumentIdWithOptions,
  createInitialTabsState,
  addTab,
  closeTab,
  updateTab,
  getActiveTab,
  clearTabHistory,
  reconcileLoadingWithRegistry,
  getDocumentId,
  setActivePresentationId,
  isStorageTabsStateFresher,
} from "../services/storageService";
import {
  getSelectedSlideRef,
  goToSlide,
  goToSlideById,
  listSlides,
} from "../services/slideService";
import {
  getActivePresentationContextViaIntents,
  getSlideCountViaIntents,
  invalidatePresentationScopedIntentBridgeCaches,
} from "../services/factset/intentBridgeService";
import {
  getActiveOperationForDocument,
  isInDeckSlideReplacementInProgress,
  isTabFlowLive,
} from "../services/inFlightTabRegistry";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import {
  adoptInitialSlideSelection,
  evaluateSlideSelectionChange,
  getSlideSelectionKey,
  reconcileTabsStateWithSlides,
  resolveTabClick,
  sortTabsBySlideIndex,
} from "../tabSyncLogic";
const ACTIVE_CONTEXT_POLL_INTERVAL_MS = 500;
const PRESENTATION_ID_PREFIX = "pres_";

function normalizeOpenPresentationId(value: string | null | undefined): string | null {
  const normalized = (value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function getOpenPresentationIdFromDocumentId(documentId: string): string | null {
  if (!documentId.startsWith(PRESENTATION_ID_PREFIX)) return null;
  return normalizeOpenPresentationId(documentId.slice(PRESENTATION_ID_PREFIX.length));
}

// Predicate used by `reconcileLoadingWithRegistry` to decide whether a tab's
// persisted `isLoading=true` is still backed by a live flow. Reads the
// coordinator's in-flight registry at call time so document-switch reloads
// and post-refresh hydration both see the most current state.
//
// IMPORTANT: in the split-phase chat architecture the LLM "thinking" phase
// runs OUTSIDE the coordinator so file 2 can think in parallel with file 1's
// queued writes. That means a tab in mid-thinking has NO entry in the
// `getInFlightRegistrySnapshot()` write-flow registry, and on a file switch
// + switch-back its persisted `isLoading=true` / streamingThinking would be
// silently demoted to "Interrupted" — which is exactly the "thinking is not
// there when switching files" bug. We therefore also consult
// `isThinkingPhaseLive` so chat thinking tickets keep the loading affordance
// alive across the round-trip.
function isFlowLiveInRegistry(documentId: string, tabId: string): boolean {
  return isTabFlowLive(documentId, tabId);
}

function reconcileLoadingForDocument(documentId: string, state: TabsState): TabsState {
  return reconcileLoadingWithRegistry(documentId, state, isFlowLiveInRegistry);
}

interface UseTabsProps {
  isAuthenticated: boolean;
  storageUserId?: string | null;
  onDocumentChange?: () => void;
  onSlideChange?: () => void;
}

export function useTabs({ isAuthenticated, storageUserId, onDocumentChange, onSlideChange }: UseTabsProps) {
  // --- State ---
  const [tabsState, setTabsState] = useState<TabsState>(() => {
    const initialDocId = getDocumentId();
    const saved = loadTabsState(storageUserId);
    if (!saved) return createInitialTabsState(initialDocId);
    // STORAGE_VERSION 3 persists isLoading/streamingThinking across reloads.
    // Demote any tab whose loading state isn't backed by a live coordinator
    // (or thinking-phase) flow so a refreshed tab doesn't spin forever.
    return reconcileLoadingForDocument(initialDocId, saved);
  });
  const tabsStateRef = useRef<TabsState>(tabsState);
  const accountScopeKey = storageUserId ?? null;
  const accountScopeRef = useRef<string | null>(accountScopeKey);
  React.useLayoutEffect(() => {
    accountScopeRef.current = accountScopeKey;
  });
  const [tabsStateAccountScope, setTabsStateAccountScope] =
    useState<string | null>(accountScopeKey);
  // Tracks which account owns the current React snapshot. During an account
  // switch, the old snapshot must not be persisted under the new scope while
  // the replacement hydration is still pending.
  const stateAccountScopeRef = useRef<string | null>(accountScopeKey);
  // Serialises the shared active-context poll so slow host round-trips cannot
  // overlap document-switch handling or selected-slide adoption.
  const activeContextPollInFlightRef = useRef(false);
  const activeContextPollPendingRef = useRef(false);
  const pendingTabNavigationRef = useRef<{ slideId: string; slideIndex: number } | null>(null);
  const [deckHasSlides, setDeckHasSlides] = useState<boolean | null>(null);
  const deckHasSlidesRef = useRef<boolean | null>(null);
  const updateDeckHasSlides = useCallback((nextDeckHasSlides: boolean | null) => {
    deckHasSlidesRef.current = nextDeckHasSlides;
    setDeckHasSlides(nextDeckHasSlides);
  }, []);
  const lastSelectedSlideRef = useRef<string | null>(null);
  const lastDocumentIdRef = useRef<string | null>(null);
  const pendingDocSwitchRef = useRef<{ documentId: string; confirmations: number } | null>(null);
  const slideByIdRef = useRef<Map<string, { index: number; slideNumber?: number }>>(new Map());
  // Which presentation's envelope the CURRENT React state belongs to. The
  // persist effect below writes the React state under THIS id rather than
  // `getDocumentId()`, because the latter can flip in a different tick when
  // `setActivePresentationId(...)` fires from the FactSet poll — and that
  // race was sending file-A's in-flight user-message / thinking updates
  // into file-B's storage envelope on a mid-flow file switch.
  const stateDocIdRef = useRef<string | null>(null);
  if (stateDocIdRef.current === null) {
    stateDocIdRef.current = getDocumentId();
  }

  // Stable refs for callbacks so polling effects don't restart on every render
  const onDocumentChangeRef = useRef(onDocumentChange);
  const onSlideChangeRef = useRef(onSlideChange);
  React.useLayoutEffect(() => {
    onDocumentChangeRef.current = onDocumentChange;
    onSlideChangeRef.current = onSlideChange;
  });

  const stateBelongsToCurrentAccount =
    tabsStateAccountScope === accountScopeKey;
  const exposedTabsState: TabsState = stateBelongsToCurrentAccount
    ? tabsState
    : {
        documentId: getDocumentId(),
        tabs: [],
        activeTabId: "",
      };
  const activeTab = getActiveTab(exposedTabsState);
  const sortedTabs = useMemo(
    () => sortTabsBySlideIndex(exposedTabsState.tabs),
    [exposedTabsState.tabs]
  );

  // Scoped to the doc active when this call started — a hydrate fired
  // for file A must not overwrite file B's React state after a switch.
  const hydrateTabsFromBackend = useCallback(async () => {
    if (!isAuthenticated) return;
    const sourceAccountScopeKey = accountScopeKey;
    if (accountScopeRef.current !== sourceAccountScopeKey) return;
    const sourceDocumentId = getDocumentId();
    const remoteState = await loadTabsStateFromBackend(
      sourceDocumentId,
      sourceAccountScopeKey
    );
    if (!remoteState) return;
    if (accountScopeRef.current !== sourceAccountScopeKey) {
      console.log("[Tabs] Discarding hydrate result — account scope changed");
      return;
    }
    if (getDocumentId() !== sourceDocumentId) {
      console.log(
        `[Tabs] Discarding hydrate result — active doc changed (${sourceDocumentId} → ${getDocumentId()})`
      );
      return;
    }
    if (tabsStateRef.current.documentId !== sourceDocumentId) {
      console.log(
        `[Tabs] Discarding hydrate result — React state belongs to ${tabsStateRef.current.documentId}, not ${sourceDocumentId}`
      );
      return;
    }
    if (stateAccountScopeRef.current !== sourceAccountScopeKey) {
      console.log("[Tabs] Discarding hydrate result — React state belongs to another account");
      return;
    }
    const reconciled = reconcileLoadingForDocument(sourceDocumentId, remoteState);
    // Same eager-save race as the doc-change handler's "save outgoing": a
    // chat flow's `applyToSourceTabs` may have updated localStorage AFTER
    // `loadTabsStateFromBackend` returned but BEFORE this commit. The
    // persist effect would then write `reconciled` (which doesn't include
    // the eager save's new message) back over the fresh local data,
    // silently dropping the user/assistant message.
    if (
      isStorageTabsStateFresher(
        sourceDocumentId,
        reconciled,
        sourceAccountScopeKey
      )
    ) {
      console.log(
        `[Tabs] Discarding hydrate result for ${sourceDocumentId} — local storage holds fresher state (in-flight eager save).`
      );
      return;
    }
    if (accountScopeRef.current !== sourceAccountScopeKey) return;
    stateAccountScopeRef.current = sourceAccountScopeKey;
    setTabsStateAccountScope(sourceAccountScopeKey);
    setTabsState(reconciled);
    tabsStateRef.current = reconciled;
  }, [accountScopeKey, isAuthenticated]);

  useLayoutEffect(() => {
    let cancelled = false;
    if (stateAccountScopeRef.current !== accountScopeKey) {
      const safeDocumentId = getDocumentId();
      const safeState = createInitialTabsState(safeDocumentId);
      stateDocIdRef.current = safeDocumentId;
      stateAccountScopeRef.current = accountScopeKey;
      tabsStateRef.current = safeState;
      setTabsStateAccountScope(accountScopeKey);
      setTabsState(safeState);
      updateDeckHasSlides(null);
    }
    // Prime the FactSet override BEFORE the initial localStorage read so
    // first load already targets the correct presentation's envelope.
    // Falls back gracefully when the bridge is unavailable: in that case
    // `getDocumentId()` reverts to the URL / session id derivation it always
    // used.
    const bootstrap = async () => {
      const sourceAccountScopeKey = accountScopeKey;
      try {
        const activeContext = await getActivePresentationContextViaIntents();
        if (
          cancelled ||
          accountScopeRef.current !== sourceAccountScopeKey
        ) {
          return;
        }
        if (activeContext.presentationRef?.openPresentationId) {
          setActivePresentationId(activeContext.presentationRef.openPresentationId);
        }
      } catch {
        // Bridge unavailable — stay on the legacy URL-derived id.
      }
      if (
        cancelled ||
        accountScopeRef.current !== sourceAccountScopeKey
      ) {
        return;
      }
      const docId = getDocumentId();
      const localRaw = loadTabsState(sourceAccountScopeKey);
      const localState = localRaw
        ? reconcileLoadingForDocument(docId, localRaw)
        : createInitialTabsState(docId);
      // CRITICAL: tag the React state with its doc id BEFORE handing it to
      // React. The persist effect reads `stateDocIdRef.current` to decide
      // where to save; setting it after `setTabsState` is racy because the
      // commit can be scheduled (React 18 batching) and our setActivePresentation
      // poll runs from a separate task.
      stateDocIdRef.current = docId;
      stateAccountScopeRef.current = sourceAccountScopeKey;
      pendingTabNavigationRef.current = null;
      setTabsStateAccountScope(sourceAccountScopeKey);
      setTabsState(localState);
      tabsStateRef.current = localState;
      lastDocumentIdRef.current = docId;
      if (isAuthenticated) {
        void hydrateTabsFromBackend();
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [
    accountScopeKey,
    isAuthenticated,
    hydrateTabsFromBackend,
    updateDeckHasSlides,
  ]);

  // --- syncTabsWithPresentation ---
  // Tab reconciliation needs slide IDs and indices ("which slides exist right
  // now?"), so it must use `listSlides()` / FactSet `GetSlideList`. That
  // intent is too expensive for background polling; keep this helper for
  // explicit user actions and known structural changes only.
  //
  // CRITICAL: `listSlides()` always targets the ACTIVE presentation, but the
  // React state may belong to a different file mid-switch (the doc-change
  // detector swaps the React state and the storage envelope, but Office's
  // active-window flip may not have settled in the same tick).
  // `reconcileTabsStateWithSlides` DROPS any tab whose `slideId` isn't in
  // the slide list, so reconciling file-A's tabs against file-B's slides
  // would silently wipe every file-A tab — and the eager-persist effect
  // then writes that wiped state back to file-A's envelope, destroying its
  // chat history. Bail out unless the state and the active presentation
  // agree.
  const syncTabsWithPresentation = useCallback(async (reason = "sync") => {
    const sourceAccountScopeKey = accountScopeRef.current;
    if (stateAccountScopeRef.current !== sourceAccountScopeKey) {
      return;
    }
    const stateDocId = stateDocIdRef.current;
    const activeDocId = getDocumentId();
    if (stateDocId && stateDocId !== activeDocId) {
      console.log(
        `[Tabs] Skipping reconcile (${reason}) — state doc ${stateDocId} !== active doc ${activeDocId}`
      );
      return;
    }
    try {
      // M2 fix: derive a presentationRef from `stateDocId` so listSlides
      // queries the file the React state belongs to, not whatever
      // PowerPoint happens to have active. Without this, a fast window
      // switch between the equality check above and the listSlides()
      // round-trip results in reconciling B's tabs against A's slide
      // list, dropping every tab whose slideId isn't in the active
      // file's slides. We only override when the docId is in the
      // `pres_*` form (FactSet path); other hosts keep the legacy
      // "target active presentation" behaviour.
      const PRESENTATION_ID_PREFIX = "pres_";
      const statePresentationRef =
        stateDocId && stateDocId.startsWith(PRESENTATION_ID_PREFIX)
          ? { openPresentationId: stateDocId.slice(PRESENTATION_ID_PREFIX.length) }
          : undefined;
      const slides = await listSlides(
        statePresentationRef ? { presentationRef: statePresentationRef } : undefined
      );
      if (
        accountScopeRef.current !== sourceAccountScopeKey ||
        stateAccountScopeRef.current !== sourceAccountScopeKey
      ) {
        console.log(`[Tabs] Discarding reconcile (${reason}) — account scope changed`);
        return;
      }
      updateDeckHasSlides(slides.length > 0);
      slideByIdRef.current = new Map(
        slides.map((slide) => [slide.id, { index: slide.index, slideNumber: slide.slideNumber }])
      );
      // Re-check after the await — the active presentation can flip while
      // we were waiting for `listSlides()` to resolve.
      const stateDocIdAfter = stateDocIdRef.current;
      const activeDocIdAfter = getDocumentId();
      if (stateDocIdAfter && stateDocIdAfter !== activeDocIdAfter) {
        console.log(
          `[Tabs] Discarding reconcile (${reason}) — active doc changed during listSlides()`
        );
        return;
      }
      setTabsState((prev) => {
        if (
          accountScopeRef.current !== sourceAccountScopeKey ||
          stateAccountScopeRef.current !== sourceAccountScopeKey
        ) {
          return prev;
        }
        const next = reconcileTabsStateWithSlides(prev, slides);
        tabsStateRef.current = next;
        if (next === prev) return prev;
        console.log(`[Tabs] Reconciled tabs (${reason})`);
        return next;
      });
    } catch (err) {
      console.warn(`[Tabs] Reconcile failed (${reason}):`, err);
    }
  }, [updateDeckHasSlides]);

  // --- Persist ---
  // Save under `tabsState.documentId` (carried INSIDE the state itself)
  // so file-A's queued chat updates can't land in file-B's envelope when
  // the FactSet poll flips presentations between commit and effect.
  useEffect(() => {
    const stateAccountScopeKey = tabsStateAccountScope;
    if (stateAccountScopeKey !== accountScopeKey) {
      return;
    }
    tabsStateRef.current = tabsState;
    const docId = tabsState.documentId || stateDocIdRef.current || getDocumentId();
    saveTabsStateForDocumentIdWithOptions(docId, tabsState, {
      accountScopeKey: stateAccountScopeKey,
    });
  }, [accountScopeKey, tabsState, tabsStateAccountScope]);

  // Deliberately no background `syncTabsWithPresentation` timer here:
  // `GetSlideList` is expensive and unsuitable for polling. Manual
  // add-tab/structural flows still call the helper on demand when they need
  // a full id/index reconciliation.

  // --- Active context polling ---
  // Why FactSet first instead of Office.context.document.url:
  //   In the FactSet PowerPoint bridge there is a SINGLE Sidebar/task pane
  //   instance per PowerPoint application that follows the user across every
  //   open file (see
  //   docs/powerpoint/identifying_presentations_from_finster_chat.md). The
  //   task pane is loaded ONCE against whatever file was active at boot, so
  //   `Office.context.document.url` is sticky to that initial file and never
  //   reflects subsequent file switches. The only signal that actually
  //   changes when the user moves between presentations is FactSet's
  //   `openPresentationId`, which we read here via the intent bridge.
  //
  //   Polling contract:
  //     1. Fetch `GetActivePresentationContext` once per visible-pane tick.
  //     2. Handle `openPresentationId` / document switching first.
  //     3. If the document changed, skip selected-slide adoption for that tick.
  //     4. If the document is stable, use the selected slide from the same
  //        active-context response to keep the active-tab indicator in sync.
  useEffect(() => {
    let cancelled = false;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;
    let runLoopInFlight = false;

    const clearPendingTimer = () => {
      if (pendingTimer !== null) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    };

    const readActiveContext = async (): Promise<{
      factSetId: string | null;
      selectedSlide: SlideRef | null;
    }> => {
      if (shouldUseFactSetBridge()) {
        const activeContext = await getActivePresentationContextViaIntents();
        return {
          factSetId: activeContext.presentationRef?.openPresentationId ?? null,
          selectedSlide: activeContext.selectedSlideRef,
        };
      }

      return {
        factSetId: null,
        selectedSlide: isAuthenticated ? await getSelectedSlideRef() : null,
      };
    };

    const handleDocumentChange = async (factSetId: string | null): Promise<boolean> => {
      try {
        // Refresh the FactSet override before reading getDocumentId() so the
        // "current" id reflects whichever presentation the user is actually
        // looking at right now.
        if (factSetId) {
          setActivePresentationId(factSetId);
        }

        const currentDocId = getDocumentId();
        if (lastDocumentIdRef.current && lastDocumentIdRef.current !== currentDocId) {
          // If FactSet gave us a concrete openPresentationId, trust it and switch
          // immediately. Keep the 2-confirmation guard only for non-FactSet /
          // fallback detection paths where transient IDs are more likely.
          const shouldRequireConfirmation = !factSetId;
          if (shouldRequireConfirmation) {
            const pendingSwitch = pendingDocSwitchRef.current;
            if (!pendingSwitch || pendingSwitch.documentId !== currentDocId) {
              pendingDocSwitchRef.current = { documentId: currentDocId, confirmations: 1 };
              return true;
            }
            if (pendingSwitch.confirmations < 2) {
              pendingDocSwitchRef.current = {
                documentId: currentDocId,
                confirmations: pendingSwitch.confirmations + 1,
              };
              return true;
            }
          }
          pendingDocSwitchRef.current = null;
          console.log(`[App] Document changed: ${lastDocumentIdRef.current} → ${currentDocId}`);
          // Drop per-presentation caches BEFORE the next selected-slide
          // adoption, otherwise a stale `selectedSlideRef` (TTL ~500ms) from
          // the OLD file can land in the NEW file's tab list.
          //
          // EXCEPT when a coordinator flow (deck or chat) is still running
          // against the OUTGOING file: the cache holds the slide-bounds
          // / slide-infos / selected-slide-ref the in-flight pipeline is
          // mid-batch on. Wiping it here forces the very next
          // `applyModificationsViaIntents` to resolve bounds against
          // whatever PowerPoint currently has active — i.e. the new file
          // the user switched TO — and the remaining slides get drawn
          // with the wrong width/height. We defer the wipe; the flow's
          // own per-mutation `resetIntentBridgeCache` calls keep its data
          // fresh, and the next manual switch after the flow ends will
          // clear stale state.
          const outgoingDocId = lastDocumentIdRef.current;
          const activeOperation = getActiveOperationForDocument(outgoingDocId);
          const flowOwnsOutgoingFile = Boolean(activeOperation);
          if (!flowOwnsOutgoingFile) {
            invalidatePresentationScopedIntentBridgeCaches();
          } else {
            console.log(
              `[App] Document changed during active ${activeOperation!.kind} operation on outgoing file; preserving intent bridge caches`
            );
          }
          // Persist the outgoing presentation's state (loading flags included
          // since STORAGE_VERSION 3) to its OWN envelope BEFORE swapping —
          // otherwise the post-swap `saveTabsState` would clobber the source
          // file's tabs with the incoming file's documentId/state.
          //
          // NOTE: we explicitly route through `saveTabsStateForDocumentId`
          // with the OLD id rather than calling `saveTabsState` (which keys
          // by the now-current id) so the outgoing file's history is
          // preserved under its own key even though the storage layer's
          // active id has already advanced.
          //
          // CRITICAL: `tabsStateRef.current` is only refreshed by the persist
          // effect AFTER React commits. A mid-thinking chat that crossed the
          // file switch will have already eager-saved its user/assistant
          // message into the outgoing file's envelope via
          // `applyToSourceTabs` — but if React hasn't committed yet,
          // `tabsStateRef.current` is the PRE-message snapshot. Saving it
          // here unconditionally would overwrite the freshly-stored message
          // (the "user message and/or assistant reply disappears after a
          // mid-thinking file switch" symptom). Skip the save when storage
          // already holds a fresher state than our React snapshot.
          const outgoingState = tabsStateRef.current;
          if (outgoingDocId && outgoingState) {
            if (
              isStorageTabsStateFresher(
                outgoingDocId,
                outgoingState,
                accountScopeKey
              )
            ) {
              console.log(
                `[App] Skipping outgoing save for ${outgoingDocId} — storage holds fresher state than React snapshot (likely a chat flow eager-save mid-switch).`
              );
            } else {
              saveTabsStateForDocumentIdWithOptions(outgoingDocId, outgoingState, {
                immediate: true,
                accountScopeKey,
              });
            }
          }

          const saved = loadTabsState(accountScopeKey);
          const nextState = saved
            ? reconcileLoadingForDocument(currentDocId, saved)
            : createInitialTabsState(currentDocId);
          // Re-tag the React state to the NEW doc id BEFORE handing it to React.
          // The persist effect uses `stateDocIdRef.current` as the storage key
          // for whatever it sees in `tabsState`; if we updated it after the
          // `setTabsState` below, the next persist tick could mis-route the
          // incoming state back to the outgoing file's envelope.
          stateDocIdRef.current = currentDocId;
          stateAccountScopeRef.current = accountScopeKey;
          updateDeckHasSlides(null);
          setTabsStateAccountScope(accountScopeKey);
          setTabsState(nextState);
          tabsStateRef.current = nextState;
          if (isAuthenticated) {
            void hydrateTabsFromBackend();
          }
          pendingTabNavigationRef.current = null;
          lastSelectedSlideRef.current = null;

          onDocumentChangeRef.current?.();
          lastDocumentIdRef.current = currentDocId;
          return true;
        }
        if (!lastDocumentIdRef.current || lastDocumentIdRef.current === currentDocId) {
          pendingDocSwitchRef.current = null;
        }
        lastDocumentIdRef.current = currentDocId;
        return false;
      } catch (e) {
        console.log("[App] Could not check document:", e);
        return false;
      }
    };

    const handleSlideSelection = (
      currentSlide: SlideRef,
      replacementWasInProgressWhenRead: boolean
    ) => {
      // If React state still belongs to another file, bail out so we don't
      // adopt the new file's slide id into the old file's tab list.
      if (stateDocIdRef.current && stateDocIdRef.current !== getDocumentId()) {
        return;
      }

      let normalizedSlide = currentSlide;
      // Some hosts occasionally return a stale selected-slide index while
      // still reporting the correct slide id. Use the local cache when it
      // already exists, but do not fetch `GetSlideList` from this polling
      // loop; that intent is reserved for explicit/on-demand reconciliation.
      if (currentSlide.id) {
        const cachedSlide = slideByIdRef.current.get(currentSlide.id);
        if (cachedSlide) {
          if (
            cachedSlide.index !== currentSlide.index ||
            (typeof cachedSlide.slideNumber === "number" &&
              cachedSlide.slideNumber !== currentSlide.slideNumber)
          ) {
            normalizedSlide = {
              ...currentSlide,
              index: cachedSlide.index,
              ...(typeof cachedSlide.slideNumber === "number"
                ? { slideNumber: cachedSlide.slideNumber }
                : {}),
            };
          }
        }
      }

      // M1 fix: the active-context result is stamped with the
      // `openPresentationId` it was queried against. If it disagrees with the
      // storage-layer doc id, drop it; document handling always runs first and
      // the next tick will fetch fresh data for the active file.
      const currentDocId = getDocumentId();
      const currentOpenPresentationId = getOpenPresentationIdFromDocumentId(currentDocId);
      const selectedOpenPresentationId = normalizeOpenPresentationId(
        normalizedSlide.openPresentationId
      );
      if (
        selectedOpenPresentationId &&
        currentOpenPresentationId &&
        selectedOpenPresentationId !== currentOpenPresentationId
      ) {
        return;
      }

      const activeStateTab = tabsStateRef.current.tabs.find(
        (tab) => tab.id === tabsStateRef.current.activeTabId
      );
      const selectedSlideNumber = normalizedSlide.slideNumber ?? normalizedSlide.index + 1;
      if (
        activeStateTab?.isLoading &&
        normalizedSlide.id &&
        activeStateTab.session.slideId !== normalizedSlide.id &&
        activeStateTab.session.slideIndex === normalizedSlide.index
      ) {
        setTabsState((prev) => {
          if (prev.documentId && prev.documentId !== getDocumentId()) {
            return prev;
          }
          const currentActiveTab = prev.tabs.find((tab) => tab.id === prev.activeTabId);
          if (
            !currentActiveTab?.isLoading ||
            !normalizedSlide.id ||
            currentActiveTab.session.slideId === normalizedSlide.id ||
            currentActiveTab.session.slideIndex !== normalizedSlide.index
          ) {
            return prev;
          }
          const next = updateTab(prev, currentActiveTab.id, {
            session: {
              ...currentActiveTab.session,
              slideId: normalizedSlide.id,
              slideIndex: normalizedSlide.index,
              slideNumber: selectedSlideNumber,
              name: `Slide ${selectedSlideNumber}`,
              updatedAt: Date.now(),
            },
          });
          tabsStateRef.current = next;
          return next;
        });
        lastSelectedSlideRef.current = getSlideSelectionKey(normalizedSlide);
        return;
      }

      if (
        replacementWasInProgressWhenRead &&
        activeStateTab?.isLoading &&
        normalizedSlide.id &&
        activeStateTab.session.slideId !== normalizedSlide.id
      ) {
        // A PresLink/full-deck refresh transiently selects a replacement slide
        // (at a shifted index) while the chat turn is still completing. ONLY
        // while that replacement is actually running do we swallow the host
        // selection so it can't create/select an empty tab over the in-flight
        // history tab. Outside a replacement we fall through to normal
        // adoption, so manually navigating to another slide still switches the
        // visible chat even while a query is loading (enables parallel queries
        // across slides). The same-index retarget guard above is intentionally
        // left ungated: its index-equality check already restricts it to
        // replacement churn and lets it self-heal the in-flight tab in place.
        //
        // Do not advance `lastSelectedSlideRef` here. If the selection was a
        // real user navigation and remains selected after replacement, the next
        // poll must reconsider and adopt it rather than treating it as already
        // handled.
        return;
      }

      const selection = evaluateSlideSelectionChange(lastSelectedSlideRef.current, normalizedSlide);

      if (selection.shouldAdoptSlide) {
        setTabsState((prev) => {
          // Same race, checked inside the commit.
          if (prev.documentId && prev.documentId !== getDocumentId()) {
            return prev;
          }
          // First load: try to promote the default placeholder tab in
          // place so we don't leave a stray "Slide 1" tab next to the
          // tab we just opened for the real current slide.
          if (selection.isInitialSelection) {
            const adopted = adoptInitialSlideSelection(prev, normalizedSlide);
            if (adopted) {
              tabsStateRef.current = adopted;
              console.log(
                `[Tabs] Adopted current slide → ${normalizedSlide.slideNumber ?? normalizedSlide.index + 1}`
              );
              return adopted;
            }
          } else {
            console.log(`Slide changed → ${normalizedSlide.slideNumber ?? normalizedSlide.index + 1}`);
          }
          const newState = addTab(prev, normalizedSlide);
          const next = newState || prev;
          if (next !== prev) {
            tabsStateRef.current = next;
          }
          return next;
        });
        // Don't fire onSlideChange on first observation — the user just
        // opened the task pane and shouldn't be bumped out of whatever
        // view they're in.
        if (!selection.isInitialSelection) {
          onSlideChangeRef.current?.();
        }
      }

      lastSelectedSlideRef.current = selection.currentSlideKey;
    };

    const checkActiveContext = async () => {
      const { factSetId, selectedSlide } = await readActiveContext();
      if (cancelled) return;

      // Snapshot replacement state against the exact presentation that
      // produced this selected-slide result. `handleDocumentChange` is async;
      // consulting the mutable registry after awaiting it could let a stale
      // transient replacement selection through if the replacement completed
      // in between.
      const observedPresentationId =
        normalizeOpenPresentationId(selectedSlide?.openPresentationId) ??
        normalizeOpenPresentationId(factSetId) ??
        getOpenPresentationIdFromDocumentId(getDocumentId());
      const replacementWasInProgressWhenRead =
        selectedSlide !== null &&
        isInDeckSlideReplacementInProgress(observedPresentationId);

      const documentChanged = await handleDocumentChange(factSetId);
      if (cancelled || documentChanged || !isAuthenticated) {
        return;
      }

      if (!selectedSlide) {
        if (shouldUseFactSetBridge()) {
          try {
            const slideCount = await getSlideCountViaIntents();
            if (!cancelled) {
              const nextDeckHasSlides = slideCount > 0;
              if (deckHasSlidesRef.current !== nextDeckHasSlides) {
                updateDeckHasSlides(nextDeckHasSlides);
              }
            }
          } catch (error) {
            console.log("[App] Could not confirm slide count:", error);
          }
        } else {
          updateDeckHasSlides(false);
        }
        return;
      }

      updateDeckHasSlides(true);
      handleSlideSelection(selectedSlide, replacementWasInProgressWhenRead);
    };

    const pollActiveContext = async (): Promise<void> => {
      if (activeContextPollInFlightRef.current) {
        activeContextPollPendingRef.current = true;
        return;
      }
      activeContextPollInFlightRef.current = true;
      try {
        await checkActiveContext();
      } catch (e) {
        console.log("[App] Could not check active context:", e);
      } finally {
        activeContextPollInFlightRef.current = false;
        if (activeContextPollPendingRef.current && !cancelled) {
          activeContextPollPendingRef.current = false;
          await pollActiveContext();
        }
      }
    };

    const runOnceAndReschedule = async () => {
      if (cancelled) return;
      if (runLoopInFlight) {
        activeContextPollPendingRef.current = true;
        return;
      }
      runLoopInFlight = true;
      clearPendingTimer();
      try {
        await pollActiveContext();
      } finally {
        runLoopInFlight = false;
        if (!cancelled && document.visibilityState === "visible") {
          pendingTimer = setTimeout(runOnceAndReschedule, ACTIVE_CONTEXT_POLL_INTERVAL_MS);
        }
      }
    };

    const triggerImmediateCheck = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      void runOnceAndReschedule();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerImmediateCheck();
      } else {
        clearPendingTimer();
      }
    };

    if (document.visibilityState === "visible") {
      triggerImmediateCheck();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", triggerImmediateCheck);
    return () => {
      cancelled = true;
      activeContextPollPendingRef.current = false;
      clearPendingTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", triggerImmediateCheck);
    };
  }, [hydrateTabsFromBackend, isAuthenticated, updateDeckHasSlides]);

  // --- updateActiveTab ---
  const updateActiveTab = useCallback(
    (updates: Partial<typeof activeTab>) => {
      if (!activeTab) return;
      setTabsState((prev) => {
        const next = updateTab(prev, activeTab.id, updates as Record<string, unknown>);
        tabsStateRef.current = next;
        return next;
      });
    },
    [activeTab]
  );

  // --- Tab handlers ---
  const handleTabClick = (tabId: string) => {
    setTabsState((prev) => {
      const { nextState, navigationTarget } = resolveTabClick(prev, tabId);
      pendingTabNavigationRef.current = navigationTarget || null;
      tabsStateRef.current = nextState;
      return nextState;
    });
  };

  useEffect(() => {
    const target = pendingTabNavigationRef.current;
    if (!target) return;
    pendingTabNavigationRef.current = null;
    if (target.slideId) {
      void goToSlideById(target.slideId);
      return;
    }
    if (target.slideIndex >= 0) {
      void goToSlide(target.slideIndex);
    }
  }, [tabsState]);

  const handleTabClose = (tabId: string) => {
    setTabsState((prev) => {
      const next = closeTab(prev, tabId);
      tabsStateRef.current = next;
      return next;
    });
  };

  const handleAddTab = async () => {
    const sourceAccountScopeKey = accountScopeRef.current;
    try {
      const slideRef = await getSelectedSlideRef();
      if (accountScopeRef.current !== sourceAccountScopeKey) return;
      setTabsState((prev) => {
        const newState = addTab(prev, slideRef);
        const next = newState || prev;
        tabsStateRef.current = next;
        return next;
      });
      await syncTabsWithPresentation("add-tab");
    } catch {
      if (accountScopeRef.current !== sourceAccountScopeKey) return;
      setTabsState((prev) => {
        const newState = addTab(prev, { id: "", index: 0 });
        const next = newState || prev;
        tabsStateRef.current = next;
        return next;
      });
      await syncTabsWithPresentation("add-tab-fallback");
    }
  };

  const handleClearHistory = () => {
    if (activeTab) {
      setTabsState((prev) => clearTabHistory(prev, activeTab.id));
    }
  };

  return {
    tabsState: exposedTabsState,
    setTabsState,
    activeTab,
    sortedTabs,
    deckHasSlides: stateBelongsToCurrentAccount ? deckHasSlides : null,
    syncTabsWithPresentation,
    updateActiveTab,
    handleTabClick,
    handleTabClose,
    handleAddTab,
    handleClearHistory,
  };
}
